import { Phone, Calculator, ExternalLink, Home, MapPin, Building2 } from 'lucide-react';
import { COMPANY_INFO, AI_SATEI_URL, AT_HOME_URL } from '@/constants/company';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const listingsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/listings#webpage`,
  url: `${siteUrl}/listings`,
  name: '販売中の物件一覧｜名古屋16区 不動産 クリアコーポレーション',
  description: '名古屋16区の販売中物件一覧。マンション・戸建・土地など豊富な物件をアットホームで公開中。株式会社クリアコーポレーションが地元密着で物件をご紹介します。',
  inLanguage: 'ja',
  about: {
    '@type': 'RealEstateAgent',
    name: '株式会社クリアコーポレーション',
    url: siteUrl,
    areaServed: '名古屋市全16区',
  },
};

const features = [
  {
    icon: Home,
    title: '豊富な物件数',
    description: '名古屋16区を中心に、多数の物件を取り扱っています。',
  },
  {
    icon: MapPin,
    title: '地域に詳しい',
    description: '地元スタッフが物件周辺の環境も詳しくご案内します。',
  },
  {
    icon: Building2,
    title: '多様な物件タイプ',
    description: 'マンション・戸建・土地など、様々な物件をご用意。',
  },
];

export default function ListingsPage() {
  return (
    <>
      <title>販売中の物件一覧｜名古屋16区 不動産 クリアコーポレーション</title>
      <meta name="description" content="名古屋16区の販売中物件一覧。マンション・戸建・土地など豊富な物件をアットホームで公開中。株式会社クリアコーポレーションが地元密着で物件をご紹介します。" />
      <meta name="keywords" content="名古屋 不動産 物件一覧, 名古屋16区 物件, 名古屋 マンション 売買, 名古屋 戸建 売買" />
      <meta property="og:title" content="販売中の物件一覧｜名古屋16区 不動産 クリアコーポレーション" />
      <meta property="og:description" content="名古屋16区の販売中物件一覧。マンション・戸建・土地など豊富な物件をアットホームで公開中。" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href="https://clearcorp.info/listings" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(listingsJsonLd) }}
      />
      <Header />
      <main className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <section className="bg-slate-900 py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              販売中の物件
            </h1>
            <p className="text-slate-300 text-lg">
              名古屋16区のおすすめ物件をご紹介します。
              <br />
              最新の物件情報はアットホームでご確認ください。
            </p>
          </div>
        </section>

        {/* Main CTA */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ExternalLink className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                アットホームで物件を探す
              </h2>
              <p className="text-slate-600 mb-8 max-w-xl mx-auto">
                最新の販売中物件は、アットホームに掲載しています。
                名古屋16区の豊富な物件から、あなたにぴったりの物件をお探しください。
              </p>
              <a
                href={AT_HOME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-10 py-5 rounded-lg transition-colors text-lg"
              >
                販売中の物件を見る
                <ExternalLink className="w-5 h-5" />
              </a>
              <p className="text-slate-400 text-sm mt-4">
                ※アットホームのサイトへ移動します
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                物件探しのポイント
              </h2>
              <p className="text-slate-600">
                当社の物件情報の特徴です
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl p-6 md:p-8 text-center"
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <feature.icon className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Inquiry Section */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                物件についてのお問い合わせ
              </h2>
              <p className="text-slate-600">
                気になる物件がございましたら、お気軽にお問い合わせください。
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="bg-white rounded-xl p-6 md:p-8 text-center shadow-sm border border-slate-100 hover:border-emerald-200 transition-colors"
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  電話で問い合わせ
                </h3>
                <p className="text-2xl font-bold text-emerald-600 mb-2">
                  {COMPANY_INFO.phone}
                </p>
                <p className="text-slate-500 text-sm">
                  受付時間：{COMPANY_INFO.businessHours}
                </p>
              </a>
              <a
                href="/contact"
                className="bg-white rounded-xl p-6 md:p-8 text-center shadow-sm border border-slate-100 hover:border-emerald-200 transition-colors"
              >
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-2xl text-slate-600"></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  メールで問い合わせ
                </h3>
                <p className="text-slate-600 mb-2">
                  お問い合わせフォームから
                </p>
                <p className="text-emerald-600 text-sm font-semibold">
                  24時間受付中
                </p>
              </a>
            </div>
          </div>
        </section>

        {/* Sell CTA */}
        <section className="py-16 md:py-24 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              物件をお持ちの方へ
            </h2>
            <p className="text-slate-300 mb-8">
              売却をご検討中の方は、まずはAI査定で価格を確認してみませんか？
              <br />
              約60秒で簡単に査定できます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={AI_SATEI_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                <Calculator className="w-5 h-5" />
                AI査定をはじめる
              </a>
              <a
                href="/services"
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-lg transition-colors"
              >
                サービスを見る
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
